"use client";

import { createContext, useContext } from "react";
import type { Collab, CollabPreview } from "@cabal/shared/domain/session/collab/types.js";
import type { ReflectionPreview } from "@cabal/shared/domain/session/reflection/types.js";
import type {
  ProductDetailResponse,
  ProductSummary,
} from "@cabal/shared/domain/session/product/types.js";
import type { Member } from "@cabal/shared/domain/session/team/member/types.js";
import type { TeamCategory } from "@cabal/shared/domain/session/team/enums.js";
import type { SessionOverviewResponse } from "@cabal/shared/domain/session/types.js";
import type {
  VersionDetailResponse,
  VersionPreview,
  VersionSummary,
} from "@cabal/shared/domain/session/version/types.js";
import type { AttachedFileView } from "@cabal/shared/domain/upload/types.js";
import type { SessionEntityKind } from "@cabal/shared/domain/session/enums.js";
import {
  MAIN_CHAT_PRODUCT_KEY,
  type ChatState,
  type FlagLiveFlashInput,
  type LiveFlashes,
  type LoadingStates,
  type MemberActivityKind,
  type MemberActivityMap,
  type NavigationState,
  type ProductReadMarkerMap,
  type ProductUnreadCollabCounts,
  type ProductsSeenMap,
  type UnreadState,
} from "@/types/frontend-types";

export type SessionCollab = Collab;

export interface SessionTeamDetails {
  name: string;
  description: string | null;
  catchPhrase: string | null;
  bootstrapPrompt: string | null;
  category: TeamCategory | null;
  /**
   * The team's artwork, as the initial-load wire already serves it: the name the
   * server resolved, the published source aspect for THAT name (null when it has
   * no `artwork` row) and the rev that cache-busts a republish. The four travel
   * together because they are one descriptor — pairing this team's name with
   * another's measurements is exactly what `resolveArtworkDescriptor` exists to
   * prevent.
   */
  resolvedArtworkKey: string;
  artworkSourceWidth: number | null;
  artworkSourceHeight: number | null;
  artworkRev: string | null;
}

export interface NavigateToProductOptions {
  replace?: boolean;
}

export interface SessionDataContextValue {
  sessionId: string;
  session: SessionOverviewResponse | null;
  teamDefaultStartingRounds: number | null;
  teamDetails: SessionTeamDetails | null;

  products: Record<string, ProductSummary>;
  versions: Record<string, VersionSummary>;
  productDetailsById: Record<string, ProductDetailResponse>;
  versionDetailsById: Record<string, VersionDetailResponse>;
  members: Record<string, Member>;
  attachedFiles: Record<string, AttachedFileView>;
  chatMessages: Record<string, SessionCollab>;
  collabs: Record<string, SessionCollab>;

  versionPreviews: Record<string, VersionPreview>;
  collabPreviews: Record<string, CollabPreview>;
  reflectionPreviews: Record<string, ReflectionPreview>;

  fromShortId: Record<string, { type: SessionEntityKind; entityId: string }>;
  toShortId: Record<string, { canonical: string; formatted: string }>;

  chatMessageIds: string[];
  chatCursor: string | null;
  chatHasMore: boolean;
  chatLoadingMore: boolean;
  collabsByProduct: Record<string, string[]>;

  navigation: NavigationState;
  chat: ChatState;

  unread: UnreadState;
  productReads: ProductReadMarkerMap;
  unreadCollabCounts: ProductUnreadCollabCounts;
  productsSeen: ProductsSeenMap;
  mainChatScopeKey: typeof MAIN_CHAT_PRODUCT_KEY;
  isCollabUnread: (collab: Collab, productId: string | null) => boolean;

  loading: LoadingStates;
  notFound: boolean;

  actions: {
    focusProduct: (productId: string) => void;
    navigateToProduct: (
      productId: string,
      options?: NavigateToProductOptions,
    ) => void;
    goBack: () => void;
    goBackN: (count: number) => number;
    selectVersion: (versionId: string | null) => void;

    setChatMode: (mode: ChatState["mode"], productId?: string) => Promise<void>;
    setChatTargetVersion: (versionId: string | null) => void;
    setChatTargetMessage: (messageId: string | null) => void;
    setChatIndicatedMessage: (messageId: string | null) => void;
    clearChatTargets: () => void;

    markProductRead: (productId: string) => Promise<void>;
    markAllChatAsRead: () => void;
    markAllCollabsAsRead: (productId: string) => void;
    markChatReadUpTo: (messageId: string) => void;
    markCollabsReadUpTo: (productId: string, messageId: string) => void;

    addOwnMessageIds: (messageIds: string[]) => void;
    clearNewMessageIds: (messageIds: string[]) => void;
    loadOlderMessages: () => Promise<void>;

    refreshProducts: (options?: { silent?: boolean }) => Promise<void>;
    refreshChat: () => Promise<void>;
    refreshCollabs: (productId: string) => Promise<void>;
    ensureProductVersions: (
      productId: string,
      limit?: number,
    ) => Promise<VersionSummary[]>;
    ensureProductCollabs: (
      productId: string,
      limit?: number,
    ) => Promise<SessionCollab[]>;
    cacheProductDetail: (input: { detail: ProductDetailResponse }) => void;
    cacheVersionDetail: (input: { detail: VersionDetailResponse }) => void;
    refreshMembers: () => Promise<void>;
    refreshSession: () => Promise<void>;

    applySessionLocalPatch: (input: {
      patch: Partial<SessionOverviewResponse>;
    }) => void;

    flagLiveFlash: (input: FlagLiveFlashInput) => void;

    markReflectionsObserved: (input: {
      versionId: string;
      productId: string;
      reflectionNotes: readonly {
        id: string;
        note: string;
        authorMember: string | null;
      }[];
    }) => void;

    markProductDetailObserved: (input: {
      productId: string;
      description: string | null;
      definitionOfDone: string | null;
      summary: string | null;
    }) => void;

    pushMemberActivity: (input: {
      memberId: string;
      kind: MemberActivityKind;
      excerpt: string;
    }) => void;
  };
}

export interface SessionLiveContextValue {
  newMessageIds: Set<string>;
  liveFlashes: LiveFlashes;
  memberActivity: MemberActivityMap;
}

export const SessionDataContext = createContext<SessionDataContextValue | null>(
  null,
);
export const SessionLiveContext = createContext<SessionLiveContextValue | null>(
  null,
);

export function useSessionDataContext(): SessionDataContextValue {
  const context = useContext(SessionDataContext);
  if (!context) {
    throw new Error(
      "useSessionDataContext must be used within SessionDataProvider",
    );
  }
  return context;
}

export function useSessionLiveContext(): SessionLiveContextValue {
  const context = useContext(SessionLiveContext);
  if (!context) {
    throw new Error(
      "useSessionLiveContext must be used within SessionDataProvider",
    );
  }
  return context;
}
